import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Ticket, ThumbsUp, ThumbsDown } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { parseApiDate } from '../lib/utils';
import { formatDistanceToNow } from 'date-fns';
import StatusBadge from '../components/tickets/StatusBadge';
import PriorityBadge from '../components/tickets/PriorityBadge';

export default function TicketsPage() {
  const { user, isStaff } = useAuth();
  const navigate    = useNavigate();
  const [tickets, setTickets]       = useState([]);
  const [statuses, setStatuses]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    api.get('/lookups/statuses').then(d => setStatuses(d.statuses || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filterStatus) qs.set('status_id', filterStatus);
    if (search)       qs.set('search', search);
    api.get(`/tickets?${qs}`)
      .then(d => setTickets(d.tickets || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filterStatus, search]);

  const showRating = !isStaff || (user?.can_view_ratings == 1);

  return (
    <div>
      <div className="page-header">
        <h2>Tickets</h2>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/tickets/new')}>
          <Plus size={14} /> New Ticket
        </button>
      </div>

      <div className="page-body">
        <div className="flex gap-3 mb-4 items-center" style={{ flexWrap: 'wrap' }}>
          <div className="search-bar flex-1" style={{ minWidth: 200 }}>
            <Search size={15} />
            <input className="form-input" placeholder="Search tickets…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="form-select" style={{ width: 160 }}
            value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div className="card" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 48, display: 'flex', justifyContent: 'center' }}><div className="spinner" /></div>
          ) : tickets.length === 0 ? (
            <div className="empty-state">
              <Ticket size={36} />
              <h3>No tickets found</h3>
              <p>{search || filterStatus ? 'Try adjusting your filters' : 'Create your first ticket to get started'}</p>
              {!search && !filterStatus && (
                <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => navigate('/tickets/new')}>
                  <Plus size={13} /> New Ticket
                </button>
              )}
            </div>
          ) : (
            <div className="table-container">
              <table className="tickets-table">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>#</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Priority</th>
                    {isStaff && <th>Submitted by</th>}
                    {isStaff && <th>Assigned to</th>}
                    {showRating && <th style={{ width: 80 }}>Rating</th>}
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map(t => (
                    <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/tickets/${t.id}`)}>
                      <td><span className="ticket-num">#{t.ticket_number}</span></td>
                      <td>
                        <div className="font-medium truncate" style={{ maxWidth: 300 }}>{t.subject}</div>
                        {t.category && <div className="text-muted text-sm">{t.category.name}</div>}
                      </td>
                      <td>{t.status && <StatusBadge status={t.status} />}</td>
                      <td>{t.priority && <PriorityBadge priority={t.priority} />}</td>
                      {isStaff && <td className="text-sm text-secondary">{t.created_by?.full_name || t.created_by?.email || '—'}</td>}
                      {isStaff && <td className="text-sm text-secondary">{t.assigned_to?.full_name || <span className="text-muted">Unassigned</span>}</td>}
                      {showRating && (
                        <td>
                          {t.rating === 'thumbs_up' && <span style={{ color: '#10b981', display:'flex', alignItems:'center', gap:3, fontSize:12 }}><ThumbsUp size={12}/> Good</span>}
                          {t.rating === 'thumbs_down' && <span style={{ color: '#ef4444', display:'flex', alignItems:'center', gap:3, fontSize:12 }}><ThumbsDown size={12}/> Poor</span>}
                        </td>
                      )}
                      <td className="text-muted text-sm">{formatDistanceToNow(parseApiDate(t.updated_at), { addSuffix: true })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
